/**
 * Geometry for the custom scroll indicator. Kept free of React and the DOM so the arithmetic can be
 * tested directly -- jsdom reports every scroll/client dimension as 0, so exercising this through a
 * rendered component would mean stubbing the very numbers under test.
 */

export enum ScrollOrientation {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

/** Thickness (px) of the indicator track measured across the scroll axis. */
export const SCROLL_INDICATOR_THICKNESS = 4;

/** Smallest thumb length (px), so it stays grabbable when the content is very long. */
export const MIN_SCROLL_THUMB_SIZE = 20;

export interface ScrollAxisMetrics {
  /** Full length of the content along the scroll axis (`scrollWidth` / `scrollHeight`). */
  scrollSize: number;
  /** Visible length of the viewport along the scroll axis (`clientWidth` / `clientHeight`). */
  clientSize: number;
  /** Current scroll offset along the axis (`scrollLeft` / `scrollTop`). */
  scrollPosition: number;
}

export interface ScrollThumb {
  /** Distance (px) from the start of the track to the thumb. */
  offset: number;
  /** Length (px) of the thumb along the scroll axis. */
  size: number;
}

export interface ScrollState {
  /** Content overflows the viewport, so the indicator should be shown. */
  isScrollable: boolean;
  /** Scrolled away from the start, so there is content back toward the left/top. */
  canScrollBackward: boolean;
  /** There is more content past the right/bottom edge. */
  canScrollForward: boolean;
}

/** Reads the axis-relevant dimensions off a scrolling element. */
export function readScrollMetrics(
  element: Pick<
    HTMLElement,
    'scrollWidth' | 'clientWidth' | 'scrollLeft' | 'scrollHeight' | 'clientHeight' | 'scrollTop'
  >,
  orientation: ScrollOrientation
): ScrollAxisMetrics {
  if (orientation === ScrollOrientation.Vertical) {
    return {
      scrollSize: element.scrollHeight,
      clientSize: element.clientHeight,
      scrollPosition: element.scrollTop,
    };
  }
  return {
    scrollSize: element.scrollWidth,
    clientSize: element.clientWidth,
    scrollPosition: element.scrollLeft,
  };
}

/**
 * Thumb geometry for the current scroll position, or `null` when the content fits and the indicator
 * should be hidden entirely.
 *
 * The thumb is sized in proportion to how much of the content is visible, floored at
 * `MIN_SCROLL_THUMB_SIZE`, then travels the leftover track so it lands flush against the far edge at
 * maximum scroll.
 *
 * Both results stay inside the track. The size is capped at the viewport, since a container narrower
 * than the minimum thumb would otherwise compute a thumb longer than its own track. The position is
 * clamped because `scrollPosition` is not guaranteed to sit within `[0, maxScroll]`: macOS and iOS
 * report negative offsets during an elastic overscroll bounce, and fractional device pixel ratios can
 * push it a hair past the end. Left unclamped, either would slide the thumb out of its track.
 */
export function computeScrollThumb({
  scrollSize,
  clientSize,
  scrollPosition,
}: ScrollAxisMetrics): ScrollThumb | null {
  if (scrollSize <= clientSize) return null;

  const size = Math.min(
    Math.max((clientSize / scrollSize) * clientSize, MIN_SCROLL_THUMB_SIZE),
    clientSize
  );
  const maxScroll = scrollSize - clientSize;
  const travel = Math.max(clientSize - size, 0);
  const progress = maxScroll > 0 ? Math.min(Math.max(scrollPosition / maxScroll, 0), 1) : 0;

  return { offset: progress * travel, size };
}

/**
 * Scroll position that lands the thumb centred on a click at `trackPosition`, or `null` when the
 * content fits and there is nothing to scroll.
 *
 * The inverse of `computeScrollThumb`: that maps a scroll offset onto the track, this maps a point
 * on the track back to a scroll offset. `trackPosition` is measured from the start of the track
 * (left edge horizontally, top edge vertically), and the track spans the viewport, so its length is
 * `clientSize`.
 *
 * Centring rather than aligning the thumb's leading edge means the content lands where the click
 * pointed instead of one thumb-length past it, and it makes the two ends reachable: a click at the
 * very start clamps to 0 and one at the very end clamps to `maxScroll`.
 */
export function computeScrollPositionForTrackClick({
  metrics,
  trackPosition,
}: {
  metrics: ScrollAxisMetrics;
  trackPosition: number;
}): number | null {
  const thumb = computeScrollThumb(metrics);
  if (thumb == null) return null;

  const { scrollSize, clientSize } = metrics;
  const maxScroll = scrollSize - clientSize;
  const travel = Math.max(clientSize - thumb.size, 0);
  // A thumb as long as its track has nowhere to travel; every click means "the start".
  if (travel <= 0) return 0;

  const desiredOffset = Math.min(Math.max(trackPosition - thumb.size / 2, 0), travel);
  return (desiredOffset / travel) * maxScroll;
}

/**
 * Scroll position for a thumb dragged `pointerDelta` px along the track from where the drag began,
 * or `null` when the content fits.
 *
 * Measured against the position the viewport held when the drag started rather than accumulated
 * per move, so the thumb tracks the pointer exactly: rounding cannot drift over a long drag, and a
 * pointer returned to its starting point returns the content there too.
 *
 * The pointer keeps producing movement after the thumb reaches either end, and clamping here is
 * what stops that overshoot from being stored. Without it a drag well past the end would have to be
 * "unwound" by the same distance before the content moved back.
 */
export function computeScrollPositionForThumbDrag({
  metrics,
  pointerDelta,
  scrollPositionAtDragStart,
}: {
  metrics: ScrollAxisMetrics;
  pointerDelta: number;
  scrollPositionAtDragStart: number;
}): number | null {
  const thumb = computeScrollThumb(metrics);
  if (thumb == null) return null;

  const { scrollSize, clientSize } = metrics;
  const maxScroll = scrollSize - clientSize;
  const travel = Math.max(clientSize - thumb.size, 0);
  // A thumb with no room to travel cannot express a drag.
  if (travel <= 0) return 0;

  // One pixel of thumb travel is worth `maxScroll / travel` pixels of content.
  const scrolled = scrollPositionAtDragStart + (pointerDelta / travel) * maxScroll;
  return Math.min(Math.max(scrolled, 0), maxScroll);
}

/**
 * Whether the viewport can scroll, and in which directions.
 *
 * `canScrollForward` allows a 1px slack: browsers report fractional scroll offsets at device pixel
 * ratios above 1, so an exact comparison leaves the forward affordance enabled at the true end of
 * the content.
 */
export function computeScrollState({
  scrollSize,
  clientSize,
  scrollPosition,
}: ScrollAxisMetrics): ScrollState {
  return {
    isScrollable: scrollSize > clientSize,
    canScrollBackward: scrollPosition > 0,
    canScrollForward: scrollPosition + clientSize < scrollSize - 1,
  };
}
